import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../../services';
import { useAppSelector } from '../../store';
import { ROLE_OPTIONS, ROLE_COLORS, UserRole } from '../../constants/users.constants';
import { PAGE_LAYOUT } from '../../constants/app.constants';

interface UserRecord {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const UsersPage: React.FC = () => {
  const currentUser = useAppSelector(state => state.auth.user);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await apiService.get<UserRecord[]>('/users');
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const isSelf = (userId: string): boolean => userId === currentUser?.id;

  const handleRoleChange = async (userId: string, role: string) => {
    if (isSelf(userId)) { toast.error('You cannot change your own role'); return; }
    try {
      await apiService.put<UserRecord>(`/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: role as UserRecord['role'] } : u));
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    if (isSelf(userId)) { toast.error('You cannot deactivate your own account'); return; }
    try {
      await apiService.put<UserRecord>(`/users/${userId}/status`, { is_active: !currentStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
      toast.success(!currentStatus ? 'User activated' : 'User deactivated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_LAYOUT.PAGE_WRAPPER}>
      <div className={PAGE_LAYOUT.WIDE_CONTAINER_NO_VERTICAL}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 border border-violet-200 px-3 py-1 text-xs font-semibold text-violet-700">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Super Admin Only
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden max-h-[70vh] overflow-y-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">User</th>
                <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Email</th>
                <th className="px-5 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Role</th>
                <th className="px-5 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                <th className="px-5 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(user => (
                <tr key={user.id} className={`transition-colors ${user.is_active ? 'hover:bg-slate-50/50' : 'bg-slate-50/30 opacity-60'}`}>
                  {/* User */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-medium text-slate-800">{user.name}</span>
                      {isSelf(user.id) && <span className="text-[9px] font-semibold uppercase text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">You</span>}
                    </div>
                  </td>
                  {/* Email */}
                  <td className="px-5 py-3">
                    <span className="text-sm text-slate-500">{user.email}</span>
                  </td>
                  {/* Role */}
                  <td className="px-5 py-3 text-center">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={isSelf(user.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold outline-none ${isSelf(user.id) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${ROLE_COLORS[user.role] || ROLE_COLORS.user}`}
                    >
                      {ROLE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3 text-center">
                    <button
                      onClick={() => handleStatusToggle(user.id, user.is_active)}
                      disabled={isSelf(user.id)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isSelf(user.id) ? 'cursor-not-allowed opacity-50' : ''} ${user.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      title={isSelf(user.id) ? 'Cannot modify your own account' : user.is_active ? 'Deactivate user' : 'Activate user'}
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${user.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  {/* Joined */}
                  <td className="px-5 py-3 text-center">
                    <span className="text-xs text-slate-400">
                      {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
